import { useCallback, useEffect, useState } from 'react';

export interface LiveFormField {
  name: string;
  type: 'text' | 'number' | 'date' | 'phone' | 'email' | 'currency' | 'time' | 'yes_no' | 'select';
  label: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
}

interface LiveFormInputProps {
  fields: LiveFormField[];
  onSubmit: (values: Record<string, string>) => void;
  disabled?: boolean;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: 'rgba(20, 40, 70, 0.9)',
  border: '1px solid rgba(126, 186, 255, 0.22)',
  borderRadius: '8px',
  color: '#f7fbff',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#8cc4ff',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '4px',
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 14px',
  border: '1px solid rgba(126, 186, 255, 0.28)',
  background: 'rgba(12, 38, 72, 0.92)',
  color: '#f7fbff',
  borderRadius: '999px',
  fontSize: '13px',
  cursor: 'pointer',
  backdropFilter: 'blur(12px)',
};

const selectedButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: 'rgba(60, 120, 200, 0.7)',
  borderColor: 'rgba(126, 186, 255, 0.6)',
};

export function LiveFormInput({ fields, onSubmit, disabled }: LiveFormInputProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    fields.forEach((f) => { initial[f.name] = ''; });
    setValues(initial);
  }, [fields]);

  const handleChange = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (disabled) return;
    onSubmit(values);
  }, [disabled, onSubmit, values]);

  const renderField = (field: LiveFormField) => {
    const value = values[field.name] || '';

    switch (field.type) {
      case 'yes_no':
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              style={value === 'yes' ? selectedButtonStyle : buttonStyle}
              onClick={() => handleChange(field.name, 'yes')}
              disabled={disabled}
            >Yes</button>
            <button
              type="button"
              style={value === 'no' ? selectedButtonStyle : buttonStyle}
              onClick={() => handleChange(field.name, 'no')}
              disabled={disabled}
            >No</button>
          </div>
        );

      case 'select':
        if (field.options && field.options.length <= 4) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {field.options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  style={value === opt.value ? selectedButtonStyle : buttonStyle}
                  onClick={() => handleChange(field.name, opt.value)}
                  disabled={disabled}
                >{opt.label}</button>
              ))}
            </div>
          );
        }
        return (
          <select
            style={{ ...inputStyle, appearance: 'auto' }}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'currency':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#8cc4ff', fontSize: '14px' }}>$</span>
            <input
              type="number"
              step="0.01"
              style={inputStyle}
              value={value}
              placeholder={field.placeholder || '0.00'}
              onChange={(e) => handleChange(field.name, e.target.value)}
              disabled={disabled}
            />
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            style={{ ...inputStyle, colorScheme: 'dark' }}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            style={{ ...inputStyle, colorScheme: 'dark' }}
            value={value}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            style={inputStyle}
            value={value}
            placeholder={field.placeholder || 'Phone number'}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            style={inputStyle}
            value={value}
            placeholder={field.placeholder || 'Email address'}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            style={inputStyle}
            value={value}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      default:
        return (
          <input
            type="text"
            style={inputStyle}
            value={value}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={disabled}
            maxLength={field.validation?.maxLength}
          />
        );
    }
  };

  if (fields.length === 0) return null;

  const isSingleYesNo = fields.length === 1 && fields[0].type === 'yes_no';

  return (
    <div style={{
      alignSelf: 'flex-start',
      width: '100%',
      maxWidth: '360px',
      background: 'rgba(6, 12, 24, 0.88)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '14px',
      padding: '12px 14px',
      boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
      backdropFilter: 'blur(12px)',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      pointerEvents: 'auto',
    }}>
      {fields.map((field) => (
        <div key={field.name}>
          <label style={labelStyle}>{field.label}</label>
          {renderField(field)}
        </div>
      ))}
      {!isSingleYesNo && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '10px',
            background: 'rgba(60, 130, 230, 0.85)',
            border: '1px solid rgba(126, 186, 255, 0.35)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          Submit
        </button>
      )}
    </div>
  );
}

export default LiveFormInput;
